import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';
import SettingsNav from './SettingsNav';

const TOGGLES = [
  { key: 'email_new_application', label: 'Email me when someone applies to a job' },
  { key: 'email_interview_reminder', label: 'Email me interview reminders' },
  { key: 'email_offer_status', label: 'Email me when an offer status changes' },
  { key: 'sms_alerts', label: 'SMS alerts' },
  { key: 'whatsapp_alerts', label: 'WhatsApp alerts' },
];

function EmployerNotificationSettings() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    api
      .get('/company/settings/notifications')
      .then((res) => setPrefs(res.data))
      .catch((err) => console.error('Failed to fetch notification preferences', err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/company/settings/notifications', prefs);
      setPrefs(res.data.notification_preferences);
      setMessage('Notification preferences saved.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} mobile />}

            <h2 className="text-xl sm:text-2xl font-bold mb-1 bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">Company Settings</h2>
            <p className="text-gray-500 text-sm mb-4">Manage your team and preferences</p>

            <SettingsNav />

            <div className="bg-white rounded-xl shadow p-5 sm:p-6">
              <h3 className="font-bold text-[#0D1B3D] mb-4">Notification preferences</h3>

              {message && (
                <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg mb-4 text-sm">{message}</div>
              )}

              {loading || !prefs ? (
                <p className="text-gray-500 text-sm">Loading...</p>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {TOGGLES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => toggle(t.key)}
                        className={`w-full flex items-center justify-between p-3.5 rounded-lg border-2 transition text-left ${
                          prefs[t.key] ? 'border-[#8B5CF6] bg-[#F5F3FF] shadow-sm shadow-purple-500/10' : 'border-gray-200 bg-white hover:border-[#C4B5FD] hover:shadow-md hover:shadow-purple-500/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${prefs[t.key] ? 'text-[#7C3AED]' : 'text-gray-600'}`}>
                          {t.label}
                        </span>
                        <span
                          className={`w-10 h-5 rounded-full relative transition shrink-0 ${
                            prefs[t.key] ? 'bg-gradient-to-r from-[#5B2EFF] to-[#FF4D8D]' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                              prefs[t.key] ? 'left-5' : 'left-0.5'
                            }`}
                          />
                        </span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white px-6 py-2.5 rounded-[18px] font-semibold text-sm shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployerNotificationSettings;